import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
} from "@mui/material";
import backgroundImage from "../../img/Parallax/chamba-bg.jpg";
import cashfree from "../../services/Cashfree";
import { useDispatch } from "react-redux";
import logo from "../../img/Parallax/chamba-logo.png";
import {
  createPaymentIntentAsync,
  submitChambaRegistrationAsync,
} from "../../features/sanawar/sanawarSlice";
import { getPaymentStatus } from "../../features/sanawar/sanawarAPI";
import PaymentStatusChamba from "./PaymentStatusChamba";
import { Upload } from "lucide-react";
import { HourglassTop } from "@mui/icons-material";
import toast from "react-hot-toast";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh",
  "Lakshadweep", "Puducherry"
];

export default function MtbChambaForm() {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    age: "",
    primaryContact: "",
    emergencyContact: "",
    race_category: "",
    tshirtSize: "",
    city: "",
    state: "",
    cycling_experience: "",
    id_proof: null,
    coupons: "",
    bloodGroup: ""
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(2499);
  const [preference, setPreference] = useState("basic");
  const [pollingInterval, setPollingInterval] = useState(null);
  const [baseAmount, setBaseAmount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponMessage, setCouponMessage] = useState({
    text: "",
    color: "text.secondary",
  });

  // ✅ 3. REMOVE OLD PRICE LOGIC useEffect
  // REPLACE WITH THIS:

  useEffect(() => {
    const isHimachali = formData.state === "Himachal Pradesh";

    let price = 0;

    if (preference === "basic") {
      price = isHimachali ? 499 : 990;
    } else if (preference === "accommodation") {
      price = isHimachali ? 990 : 1499;
    } else if (preference === "accommodation_merch") {
      price = isHimachali ? 1990 : 2490;
    }

    setBaseAmount(price);

    if (isCouponApplied) {
      const discounted = Math.max(
        0,
        price - (price * discount) / 100
      );
      setFinalAmount(discounted);
    } else {
      setFinalAmount(price);
    }
  }, [preference, formData.state, isCouponApplied, discount]);




  const handleApplyCoupon = () => {
    const code = coupon.trim().toUpperCase().replace(/\s+/g, "");

    if (!code) {
      setCouponMessage({
        text: "⚠️ Please enter coupon code",
        color: "red",
      });
      return;
    }

    // All available coupons
    const coupons = {
      CCA20: 20,
      XCM50: 50,
      FREE100: 100,
      NAT100: 100,
      MTBS100: 100,
      STA50: 50,
      CAHP50: 50,
      YTP100: 100,
      YTP50: 50,
      GD30: 30,
      AF50: 50,
    };

    const discountPercent = coupons[code];

    if (!discountPercent) {
      setCouponMessage({
        text: "❌ Invalid coupon code",
        color: "red",
      });
      return;
    }

    const discounted = Math.max(
      0,
      baseAmount - (baseAmount * discountPercent) / 100
    );

    setDiscount(discountPercent);
    setIsCouponApplied(true);
    setFinalAmount(discounted);

    setCouponMessage({
      text: `✅ ${discountPercent}% coupon applied`,
      color: "green",
    });
  };


  const handleRemoveCoupon = () => {
    setCoupon("");
    setDiscount(0);
    setIsCouponApplied(false);
    setCouponMessage({
      text: "Coupon removed",
      color: "orange",
    });
    setFinalAmount(baseAmount); // restore original price
  };




  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const phone = formData.primaryContact;

    if (!phone || phone.length !== 10) {
      alert("Please enter a valid 10-digit contact number.");
      setLoading(false);
      return;
    }

    const payAmount = finalAmount;
    const appliedCoupon = coupon || null;

    /** 🟢 CASE 1: FREE REGISTRATION */
    if (payAmount <= 0) {
      try {
        await dispatch(
          submitChambaRegistrationAsync({
            ...formData,
            accommodation: preference,
            amount: finalAmount,
            coupons: appliedCoupon,
            paymentID: null,
          })
        ).unwrap();

        setPaymentSuccess(true);
      } catch (err) {
        setPaymentSuccess(false);
      }
      return;
    }


    /** 💳 STEP 2: Open Cashfree Gateway */
    const openCashfreeGateway = async (sessionId, order_id) => {
      try {
        await cashfree.checkout({
          paymentSessionId: sessionId,
          redirectTarget: "_modal",
        });

        // ✅ Do NOT depend on result?.paymentDetails
        // Immediately start polling the backend for final payment status
        startPollingPaymentStatus(order_id);
      } catch (error) {
        console.log("Cashfree checkout error:", error);
      }
    };

    /** 🔄 STEP 3: Poll backend every 3s to check payment status */
    const startPollingPaymentStatus = (order_id) => {
      const startTime = Date.now();
      const maxDuration = 15 * 60 * 1000; // 15 minutes in ms
      const intervalTime = 3000; // 3 seconds

      const intervalId = setInterval(async () => {
        const elapsed = Date.now() - startTime;


        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}race/status/${order_id}`
          );
          const data = await res.json();

          // ✅ Backend structure: { success: true, LinkID: "...", Status: "SUCCESS" }

          if (data?.success && data?.Status === "SUCCESS") {
            clearInterval(intervalId);
            setPollingInterval(null);

            toast.success("Payment confirmed successfully!");
            setPaymentSuccess(true);
            return;
          }


          // Stop after 15 minutes (timeout)
          if (elapsed >= maxDuration) {
            clearInterval(intervalId);
            setPollingInterval(null);
            setPaymentSuccess(true);
            toast.error("Payment verification timed out after 15 minutes.");
          }
        } catch (err) {
          console.error("❌ Error checking payment status:", err);
          // Do NOT clear interval on transient errors — allow retry in next cycle
        }
      }, intervalTime);

      setPollingInterval(intervalId);
    };


    /** 💳 CASE 2: PAID REGISTRATION */

    const registration = await dispatch(
      submitChambaRegistrationAsync({
        ...formData,
        accommodation: preference,
        amount: finalAmount,
        coupons: appliedCoupon,
        paymentID: null,
      })
    ).unwrap();

    setSessionInfo(registration);


    if (registration?.payment_session_id) {
      const order_id = registration?.order_id;
      const sessionId = registration?.payment_session_id;
      openCashfreeGateway(sessionId, order_id);
      return;
    }



  };


  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100dvh",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: { xs: 0, md: 3 },
        overflowY: "auto",
        WebkitOverflowScrolling: "touch", // ✅ smooth scroll on iOS
        paddingBottom: "env(safe-area-inset-bottom, 16px)",
      }}
    >
      {!modalOpen && paymentSuccess === null && (
        <Box
          sx={{
            width: "100%",
            maxWidth: 1000,
            bgcolor: "white",
            borderRadius: { xs: 0, md: 4 },
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            overflowY: "auto",
            maxHeight: { xs: "100dvh", md: "90vh" },
            WebkitOverflowScrolling: "touch",
            pb: "calc(env(safe-area-inset-bottom, 20px) + 80px)",
          }}
        >
          {/* Gradient Banner */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              width: "100%",
              py: { xs: 1.2, md: 1.5 },
              background: "linear-gradient(90deg, #2563eb, #7e22ce, #db2777)",
              color: "white",
              fontWeight: 800,
              fontSize: { xs: "1rem", sm: "1.2rem", md: "1.3rem" },
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              zIndex: 10,
            }}
          >
            MTB Chamba 2026
          </Box>

          {/* Header */}
          <Box>
            {/* Title + Logo + Organizer */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: "center",
                px: { xs: 3, md: 5 },
                py: { xs: 2, md: 3 },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              {/* Left Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 150, md: 120 },
                    height: { xs: "auto", md: "auto" },
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    // overflow: "hidden",
                  }}
                >
                  <img
                    src={logo}
                    alt="MTB Chamba Logo"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: { xs: "center", md: "flex-start" },
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: "#0f172a",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      fontSize: { xs: "22px", sm: "26px", md: "28px" },
                      lineHeight: 1.3,
                    }}
                  >
                    Race Registration
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      mt: 0.5,
                      color: "#4b5563",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      letterSpacing: "0.3px",
                    }}
                  >
                    MTB Chamba • 2026
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      mt: 0.3,
                      color: "#6b7280",
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                    }}
                  >
                    Location: <b>Chamba, Himachal Pradesh</b>
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      mt: 0.3,
                      color: "#6b7280",
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    One Stage Cross Country Marathon (XCM)
                  </Typography>
                </Box>
              </Box>

              {/* Right Section */}
              <Typography
                variant="body2"
                sx={{
                  mt: { xs: 2, md: 0 },
                  color: "#4b5563",
                  fontStyle: "italic",
                  fontSize: { xs: "13px", sm: "14px", md: "15px" },
                  textAlign: { xs: "center", md: "right" },
                  lineHeight: 1.7,
                }}
              >
                Date:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#111827",
                    whiteSpace: "nowrap",
                  }}
                >
                  March 21-22
                </Box>{" "}
                <br />
                Organized by{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: "var(--color-blue)",
                    fontStyle: "normal",
                    letterSpacing: "0.3px",
                  }}
                >
                  Chamba Cycling Association
                </Box>
              </Typography>
            </Box>
          </Box>

          {/* Form */}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: { xs: 3, md: 5 } }}
          >
            <Box
              className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 3,
              }}
            >
              <TextField
                label="Full Name"
                name="name"
                required
                fullWidth
                value={formData.name}
                onChange={handleChange}
              />
              <TextField
                label="Age as on 1st March 2026"
                name="age"
                type="number"
                required
                fullWidth
                value={formData.age}
                onChange={handleChange}
              />
              <TextField
                label="Date of Birth"
                name="dob"
                type="date"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.dob}
                onChange={handleChange}
                inputProps={{
                  max: new Date(Date.now() - 86400000)
                    .toISOString()
                    .split("T")[0],
                }}
              />
              <TextField
                label="Contact Number"
                name="primaryContact"
                required
                fullWidth
                value={formData.primaryContact}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    primaryContact: e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10),
                  })
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                helperText={
                  formData.primaryContact &&
                    formData.primaryContact.length > 0 &&
                    formData.primaryContact.length < 10
                    ? "Contact number must be 10 digits"
                    : " "
                }
              />
              <TextField
                label="Emergency Contact"
                name="emergencyContact"
                required
                fullWidth
                value={formData.emergencyContact}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emergencyContact: e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10),
                  })
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
                helperText={
                  formData.emergencyContact &&
                    formData.emergencyContact.length > 0 &&
                    formData.emergencyContact.length < 10
                    ? "Emergency number must be 10 digits"
                    : " "
                }
              />
              <FormControl required fullWidth>
                <InputLabel>Race Category</InputLabel>
                <Select
                  name="race_category"
                  value={formData.race_category}
                  onChange={handleChange}
                  label="Race Category"
                >
                  <MenuItem value="Men Sub-Junior (Under 16)">
                    Men Sub-Junior (Under 16)
                  </MenuItem>
                  <MenuItem value="Men Junior (Under 19)">
                    Men Junior (Under 19)
                  </MenuItem>
                  <MenuItem value="Men Master (Aged 36 or above)">Men Master (Aged 36 or above)</MenuItem>
                  <MenuItem value="Men Open (Under 36)">Men Open (Under 36)</MenuItem>
                  <MenuItem value="Women Sub-Junior (Under 16)">
                    Women Sub-Junior (Under 16)
                  </MenuItem>
                  <MenuItem value="Women Junior (Under 19)">
                    Women Junior (Under 19)
                  </MenuItem>
                  <MenuItem value="Women Master (Aged 36 or above)">Women Master (Aged 36 or above)</MenuItem>
                  <MenuItem value="Women Open (Under 36)">Women Open (Under 36)</MenuItem>
                </Select>
              </FormControl>
              <FormControl required fullWidth>
                <InputLabel>T-shirt Size</InputLabel>
                <Select
                  name="tshirtSize"
                  value={formData.tshirtSize}
                  onChange={handleChange}
                  label="T-shirt Size"
                >
                  {["XS", "S", "M", "L", "XL"].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="City"
                name="city"
                required
                fullWidth
                value={formData.city}
                onChange={handleChange}
              />
              <FormControl required fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  name="state"
                  value={formData.state}
                  label="State"
                  onChange={handleChange}
                >
                  {INDIAN_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Cycling Experience + Blood Group */}
              <Box
                sx={{
                  gridColumn: { xs: "span 1", md: "span 3" },
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: { xs: 3, md: 4 },
                  alignItems: "stretch",
                }}
              >
                {/* Cycling Experience */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, mb: 1, color: "#374151" }}
                  >
                    Your Best Cycling Experience
                  </Typography>
                  <TextareaAutosize
                    name="cycling_experience"
                    required
                    minRows={2}
                    placeholder="Describe your best cycling experience"
                    value={formData.cycling_experience}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      fontSize: "1rem",
                      color: "black",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      resize: "vertical",
                    }}
                  />
                </Box>

                {/* Blood Group */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "30%" }, // ✅ full width on mobile, 30% on desktop
                    display: "flex",
                    alignItems: "flex-end", // ✅ aligns with textarea bottom
                  }}
                >
                  <FormControl fullWidth required>
                    <InputLabel>Blood Group</InputLabel>
                    <Select
                      name="bloodGroup"
                      value={formData.bloodGroup}
                      label="Blood Group"
                      onChange={handleChange}
                    >
                      {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map(
                        (g) => (
                          <MenuItem key={g} value={g}>
                            {g}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* File Upload + Coupon Section */}
              <Box
                className="flex flex-col md:flex-row justify-between items-start md:col-span-3 gap-5 text-black-1"
                sx={{
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  mt: 2,
                }}
              >
                {/* Left: File Upload */}
                <Box className="w-full flex flex-col md:flex-row items-start gap-3 flex-1">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ID Proof (Aadhar / Voter ID / License)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mb: 1,
                        display: "block",
                        fontSize: "0.8rem",
                      }}
                    >
                      Upload PDF, image, or document file (Max 1MB)
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Upload size={18} />}
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      borderRadius: "8px",
                      textTransform: "none",
                      color: "var(--color-blue)",
                      border: "1px solid var(--color-blue)",
                      px: 3,
                      py: 1,
                      "&:hover": {
                        bgcolor: "rgba(37,99,235,0.08)",
                      },
                    }}
                  >
                    Upload File
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      name="id_proof"
                      onChange={handleChange}
                      hidden
                    />
                  </Button>

                  {formData.id_proof && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: { xs: 1, md: 0.5 } }}
                    >
                      {formData.id_proof.name}
                    </Typography>
                  )}
                </Box>

                {/* Right: Coupon Input */}
                <Box className="flex flex-col sm:flex-row items-start gap-3 w-full md:w-[40%]">
                  <FormControl
                    fullWidth
                    sx={{ position: "relative", width: "100%" }}
                  >
                    <TextField
                      label="Coupon Code"
                      variant="outlined"
                      name="coupons"
                      fullWidth
                      value={coupon}
                      onChange={(e) =>
                        setCoupon(
                          e.target.value.toUpperCase().replace(/\s+/g, "")
                        )
                      }
                      inputProps={{ style: { textTransform: "uppercase" } }}
                      helperText={couponMessage.text}
                      FormHelperTextProps={{
                        sx: {
                          mt: 0.5,
                          fontWeight: discount > 0 ? 600 : 500,
                          color:
                            couponMessage.color === "green"
                              ? "green"
                              : couponMessage.color === "red"
                                ? "red"
                                : "text.secondary",
                          fontSize: { xs: "0.8rem", sm: "0.65rem" },
                          transition: "all 0.3s ease",
                        },
                      }}
                    />
                  </FormControl>

                  {!isCouponApplied ? (
                    <Button
                      variant="contained"
                      onClick={handleApplyCoupon}
                      sx={{
                        height: "56px",
                        px: 4,
                        bgcolor: "var(--color-blue)",
                        color: "white",
                        fontWeight: 600,
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Apply
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={handleRemoveCoupon}
                      sx={{
                        height: "56px",
                        px: 4,
                        borderColor: "red",
                        color: "red",
                        fontWeight: 600,
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Remove
                    </Button>
                  )}

                </Box>
              </Box>

              {/* Accommodation Option */}
              <Box className="flex flex-col md:flex-row items-start gap-4 md:col-span-3">
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111827" }}
                >
                  Participation Preference
                </Typography>

                <Box className="flex flex-col gap-3 text-black-1">

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preference"
                      value="basic"
                      checked={preference === "basic"}
                      onChange={() => setPreference("basic")}
                      className="accent-blue w-5 h-5"
                    />
                    <Typography variant="body2">
                      Basic Entry
                    </Typography>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preference"
                      value="accommodation"
                      checked={preference === "accommodation"}
                      onChange={() => setPreference("accommodation")}
                      className="accent-blue w-5 h-5"
                    />
                    <Typography variant="body2">
                      With Accommodation
                    </Typography>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preference"
                      value="accommodation_merch"
                      checked={preference === "accommodation_merch"}
                      onChange={() => setPreference("accommodation_merch")}
                      className="accent-blue w-5 h-5"
                    />
                    <Typography variant="body2">
                      With Accommodation & Merchandise
                    </Typography>
                  </label>

                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      color: "#16a34a",
                      fontWeight: 500,
                    }}
                  >
                    Special concession for Himachali participants as part of our
                    initiative to promote local cycling talent.
                  </Typography>
                </Box>
              </Box>

              {/* Declaration */}
              <Box className="flex items-start gap-2 md:col-span-3">
                <input
                  type="checkbox"
                  required
                  name="declaration"
                  onChange={handleChange}
                  className="mt-1 accent-blue w-5 h-5"
                />
                <Typography variant="body2" sx={{ color: "#4b5563" }}>
                  I declare that the information provided is true to the best of
                  my knowledge. I confirm I am physically fit to participate,
                  and the organizers are not responsible for any accident,
                  injury, or loss.
                </Typography>
              </Box>

              {/* Submit Button */}
              {/* Submit Section */}
              <Box className="flex flex-col sm:flex-row justify-end items-center md:col-span-3 mt-4 gap-3 sm:gap-5">
                {/* Amount to Pay */}
                <Typography
                  variant="h6"
                  sx={{
                    color: "#111827",
                    fontWeight: 700,
                    textAlign: { xs: "center", sm: "right" },
                    mb: { xs: 1, sm: 0 },
                    fontSize: { xs: "1.2rem", md: "1.2rem" },
                  }}
                >
                  Amount to Pay:{" "}
                  <Box
                    component="span"
                    sx={{
                      color: "var(--color-blue)",
                      fontSize: "1.3rem",
                      ml: 0.5,
                    }}
                  >
                    ₹{finalAmount}
                  </Box>
                </Typography>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    bgcolor: "var(--color-blue)",
                    px: 5,
                    py: 1.3,
                    fontWeight: 600,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "1rem",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    transition: "background-color 0.3s ease",
                    "&:hover": { bgcolor: "var(--color-blue-hover)" },
                    letterSpacing: "2px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "white", // 👈 makes icon + text white
                      }}
                    >
                      <HourglassTop className="animate-spin" size={20} />
                      Processing...
                    </Box>
                  ) : (
                    "Pay & Register"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {(modalOpen || paymentSuccess !== null) && (
        <div className="w-full max-sm:min-h-screen sm:h-[600px] max-w-full md:max-w-[650px] mx-auto rounded-lg shadow-lg relative bg-white-1 overflow-y-auto">
          {modalOpen && paymentSuccess === null && (
            <>
              <div className="absolute inset-0 flex justify-center items-center z-0">
                <HourglassTop
                  fontSize="large"
                  className="animate-spin text-blue"
                />
              </div>
              <div
                id="cashfree-payment"
                className="w-full h-full md:w-[650px] md:h-[600px]"
                style={{ borderRadius: "10px" }}
              ></div>
            </>
          )}

          {paymentSuccess !== null && (
            <PaymentStatusChamba
              success={paymentSuccess}
              finalAmount={finalAmount}
              paymentID={sessionInfo?.order_id}
              formData={formData}
            />
          )}
        </div>
      )}
    </Box>
  );
}
